import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const mitigationFieldCompletionConfigurationValueSchema = z.strictObject({
    panelEnabled: z.boolean(),
    includeMaterialMoisturePoints: z.boolean(),
    includeAffectedAir: z.boolean(),
    includeUnaffectedAir: z.boolean(),
    includeExteriorAir: z.boolean(),
    requireMoistureEvidence: z.boolean(),
    requireEquipmentEvidence: z.boolean(),
    requireAmbientEvidence: z.boolean(),
    allowVerifiedWeatherWithoutEvidence: z.boolean(),
    minimumEvidenceCount: z.number().int().min(1).max(20),
    maximumItemsPerSession: z.number().int().min(1).max(2_000),
    maximumAssetPlacementsPerSession: z.number().int().min(0).max(500),
    maximumTreatmentRecordsPerProject: z.number().int().min(1).max(20_000),
    maximumClearanceRecordsPerProject: z.number().int().min(1).max(20_000),
    requirePassedClearanceEvidence: z.boolean(),
});
export const mitigationFieldCompletionConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'mitigation.field-completion.configuration',
        moduleId: 'mitigation',
        title: 'Mitigation field-completion configuration',
        helpText: 'Controls bounded daily-monitoring requirements and retained treatment and clearance evidence.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelEnabled: true,
            includeMaterialMoisturePoints: true,
            includeAffectedAir: true,
            includeUnaffectedAir: true,
            includeExteriorAir: true,
            requireMoistureEvidence: true,
            requireEquipmentEvidence: true,
            requireAmbientEvidence: true,
            allowVerifiedWeatherWithoutEvidence: true,
            minimumEvidenceCount: 1,
            maximumItemsPerSession: 1_000,
            maximumAssetPlacementsPerSession: 100,
            maximumTreatmentRecordsPerProject: 5_000,
            maximumClearanceRecordsPerProject: 5_000,
            requirePassedClearanceEvidence: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: mitigationFieldCompletionConfigurationValueSchema,
});
export const mitigationFieldCompletionDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    historyLimit: z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(250)]),
    defaultSection: z.enum(['daily-monitoring', 'treatment', 'clearance']),
    showVoidedRecords: z.boolean(),
});
export const mitigationFieldCompletionDisplaySetting = definePlatformSetting({
    definition: {
        key: 'mitigation.field-completion.display',
        moduleId: 'mitigation',
        title: 'Mitigation field-completion display',
        helpText: 'Controls field-completion card size, density, starting section, history depth, and retained void visibility.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            density: 'comfortable',
            cardSize: 'standard',
            historyLimit: 100,
            defaultSection: 'daily-monitoring',
            showVoidedRecords: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: mitigationFieldCompletionDisplayValueSchema,
});
const label = z.string().trim().min(1).max(240);
const message = z.string().trim().min(1).max(600);
export const mitigationFieldCompletionCopyValueSchema = z.strictObject({
    panelTitle: label,
    panelDescription: message,
    dailyMonitoringTab: label,
    treatmentTab: label,
    clearanceTab: label,
    workDateLabel: label,
    reasonLabel: label,
    startSessionAction: label,
    submitSessionAction: label,
    overrideSessionAction: label,
    overrideReasonLabel: label,
    noSessionLabel: message,
    noItemsLabel: message,
    dayLabel: label,
    statusLabel: label,
    completedLabel: label,
    missingLabel: label,
    notCheckedLabel: label,
    missingMeasurementLabel: label,
    missingEvidenceLabel: label,
    checkedLabel: label,
    moistureValueLabel: label,
    temperatureLabel: label,
    relativeHumidityLabel: label,
    grainsPerPoundLabel: label,
    evidenceKindLabel: label,
    evidenceIdLabel: label,
    evidenceLabelLabel: label,
    platformStorageEvidenceLabel: label,
    moistureEvidenceLabel: label,
    addEvidenceAction: label,
    removeEvidenceAction: label,
    saveItemAction: label,
    notesLabel: label,
    assetFallbackLabel: message,
    treatmentKindLabel: label,
    antimicrobialLabel: label,
    containmentLabel: label,
    productLabel: label,
    locationLabel: label,
    clientAcknowledgedLabel: label,
    pressureLabel: label,
    pressureUnitLabel: label,
    pascalLabel: label,
    inchWaterColumnLabel: label,
    occurredAtLabel: label,
    correctsTreatmentLabel: label,
    recordTreatmentAction: label,
    voidTreatmentAction: label,
    noTreatmentLabel: message,
    clearanceStatusLabel: label,
    clearanceMethodLabel: label,
    assessorNameLabel: label,
    assessorCompanyLabel: label,
    assessmentDateLabel: label,
    conditionAchievedLabel: label,
    conditionOneLabel: label,
    conditionTwoLabel: label,
    conditionThreeLabel: label,
    correctsClearanceLabel: label,
    recordClearanceAction: label,
    voidClearanceAction: label,
    noClearanceLabel: message,
    loadingLabel: label,
    loadError: message,
    mutationError: message,
    completenessError: message,
    submittedLabel: label,
    overrideSubmittedLabel: label,
    openLabel: label,
    passedLabel: label,
    failedLabel: label,
    pendingLabel: label,
    voidedLabel: label,
    moisturePointLabel: label,
    equipmentCheckLabel: label,
    affectedAirLabel: label,
    unaffectedAirLabel: label,
    exteriorAirLabel: label,
    visualMethodLabel: label,
    airSampleMethodLabel: label,
    surfaceSampleMethodLabel: label,
    clearanceLetterMethodLabel: label,
    selectPrompt: label,
    newRecordLabel: label,
    verifiedWeatherLabel: label,
    fieldMeasurementLabel: label,
    weatherProviderReferenceLabel: label,
    weatherObservedAtLabel: label,
    percentUnitLabel: label,
    relativeUnitLabel: label,
    voidReasonPrompt: message,
});
export const mitigationFieldCompletionCopySetting = definePlatformSetting({
    definition: {
        key: 'mitigation.copy.field-completion',
        moduleId: 'mitigation',
        title: 'Mitigation field-completion interface copy',
        helpText: 'Controls tenant-editable daily monitoring, treatment, and clearance labels and guidance.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelTitle: 'Mitigation field completion',
            panelDescription: 'Complete daily monitoring, retain treatment evidence, and document mold clearance without erasing history.',
            dailyMonitoringTab: 'Daily monitoring',
            treatmentTab: 'Treatment and containment',
            clearanceTab: 'Mold clearance / PRV',
            workDateLabel: 'Work date',
            reasonLabel: 'Record reason',
            startSessionAction: 'Start daily monitoring',
            submitSessionAction: 'Submit completed monitoring',
            overrideSessionAction: 'Submit with documented override',
            overrideReasonLabel: 'Why required evidence is missing',
            noSessionLabel: 'No daily monitoring session exists for this work date.',
            noItemsLabel: 'This session has no configured monitoring requirements.',
            dayLabel: 'Drying day',
            statusLabel: 'Status',
            completedLabel: 'complete',
            missingLabel: 'missing',
            notCheckedLabel: 'Not checked',
            missingMeasurementLabel: 'Measurement missing',
            missingEvidenceLabel: 'Evidence missing',
            checkedLabel: 'Checked',
            moistureValueLabel: 'Moisture reading',
            temperatureLabel: 'Temperature °F',
            relativeHumidityLabel: 'Relative humidity %',
            grainsPerPoundLabel: 'Grains per pound',
            evidenceKindLabel: 'Evidence type',
            evidenceIdLabel: 'Evidence ID',
            evidenceLabelLabel: 'Evidence label',
            platformStorageEvidenceLabel: 'Stored file',
            moistureEvidenceLabel: 'Moisture evidence',
            addEvidenceAction: 'Add evidence reference',
            removeEvidenceAction: 'Remove evidence',
            saveItemAction: 'Save monitoring item',
            notesLabel: 'Notes',
            assetFallbackLabel: 'Assets is unavailable or not permitted. Daily monitoring remains usable without equipment references.',
            treatmentKindLabel: 'Record type',
            antimicrobialLabel: 'Antimicrobial application',
            containmentLabel: 'Containment / negative pressure',
            productLabel: 'Product',
            locationLabel: 'Location',
            clientAcknowledgedLabel: 'Client Right-to-Know acknowledged',
            pressureLabel: 'Pressure reading',
            pressureUnitLabel: 'Pressure unit',
            pascalLabel: 'Pascal',
            inchWaterColumnLabel: 'Inches water column',
            occurredAtLabel: 'Occurred at',
            correctsTreatmentLabel: 'Corrects retained treatment record',
            recordTreatmentAction: 'Record treatment',
            voidTreatmentAction: 'Void treatment record',
            noTreatmentLabel: 'No treatment or containment records have been retained.',
            clearanceStatusLabel: 'Result',
            clearanceMethodLabel: 'Assessment method',
            assessorNameLabel: 'Assessor name',
            assessorCompanyLabel: 'Assessor company',
            assessmentDateLabel: 'Assessment date',
            conditionAchievedLabel: 'Condition achieved',
            conditionOneLabel: 'Condition 1',
            conditionTwoLabel: 'Condition 2',
            conditionThreeLabel: 'Condition 3',
            correctsClearanceLabel: 'Corrects retained clearance record',
            recordClearanceAction: 'Record clearance',
            voidClearanceAction: 'Void clearance record',
            noClearanceLabel: 'No post-remediation verification has been retained.',
            loadingLabel: 'Loading Mitigation field completion…',
            loadError: 'Mitigation field completion could not be loaded.',
            mutationError: 'The Mitigation field record could not be saved. Refresh and try again.',
            completenessError: 'Required monitoring evidence is missing. Complete it or provide an authorized override reason.',
            submittedLabel: 'Submitted',
            overrideSubmittedLabel: 'Override submitted',
            openLabel: 'Open',
            passedLabel: 'Passed',
            failedLabel: 'Failed',
            pendingLabel: 'Pending',
            voidedLabel: 'Voided',
            moisturePointLabel: 'Moisture point',
            equipmentCheckLabel: 'Equipment check',
            affectedAirLabel: 'Affected area air',
            unaffectedAirLabel: 'Unaffected area air',
            exteriorAirLabel: 'Exterior air',
            visualMethodLabel: 'Visual',
            airSampleMethodLabel: 'Air sample',
            surfaceSampleMethodLabel: 'Surface sample',
            clearanceLetterMethodLabel: 'Clearance letter',
            selectPrompt: 'Select',
            newRecordLabel: 'New retained record',
            verifiedWeatherLabel: 'Verified weather observation',
            fieldMeasurementLabel: 'Field measurement',
            weatherProviderReferenceLabel: 'Weather provider reference',
            weatherObservedAtLabel: 'Weather observed at',
            percentUnitLabel: 'Percent',
            relativeUnitLabel: 'Relative scale',
            voidReasonPrompt: 'Explain why this retained record must be voided (at least 10 characters).',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: mitigationFieldCompletionCopyValueSchema,
});
export const mitigationFieldCompletionSettingRegistrations = [
    mitigationFieldCompletionConfigurationSetting,
    mitigationFieldCompletionDisplaySetting,
    mitigationFieldCompletionCopySetting,
];
//# sourceMappingURL=mitigation-field-completion-setting-definitions.js.map